import { useEffect } from 'react'
import type { JSX } from 'react'
import { useDispatch } from 'react-redux'

import { Button, Dialog, Text, VisuallyHidden } from '@buffer-mono/popcorn'

import { useUser } from '../../../../../common/context/User'
import { MODALS } from '../../../../../common/types'
import {
  getBillingPageUrl,
  redirectToUrl,
} from '../../../../../common/utils/urls'
import {
  isFreeUser,
  isOnActiveTrial,
  isOneBufferOrganization,
} from '../../../../../common/utils/user'
import { resetChannelConnectionsState } from '../../../store/channelConnectionsReducer'
import { InstagramAuthMethod, Service } from '../../types'
import type { AuthMethods } from '../../types'
import { RESTRICTED_SERVICES_FOR_MP_USERS } from '../../../../../common/constants/channels'
import PaywallPromptChannels from './PaywallPromptChannels'
import type { PaywallService } from './PaywallPromptChannels'
import { useModalManager } from '../../../../../components/ModalManager/hooks/useModalManager'
import { trackUpgradePathViewed } from '../../../../../common/hooks/useSegmentTracking'
import UpgradePathsTracking from '../../../../../tracking/UpgradePaths'

function isMPRestricted<T extends Service>(
  isOneBufferOrg: boolean,
  service?: T,
  authMethod?: AuthMethods[T],
): boolean {
  if (isOneBufferOrg) {
    return false
  }

  if (service) {
    return !!(
      RESTRICTED_SERVICES_FOR_MP_USERS.includes(service) ||
      (service === Service.instagram &&
        authMethod === InstagramAuthMethod.notificationsOnly)
    )
  }

  return false
}

const PaywallModal = <T extends Service>({
  service,
  authMethod,
  onClose,
}: {
  service?: T
  authMethod?: AuthMethods[T]
  serviceName?: string
  onClose?: () => void
}): JSX.Element => {
  const user = useUser()
  const { openModal } = useModalManager()

  const isOneBufferOrg = isOneBufferOrganization(user)
  const isFreeNewBufferPlan = isFreeUser(user)
  const isOnTrial = isOnActiveTrial(user)
  const isMPRestrictedService = isMPRestricted(
    isOneBufferOrg,
    service,
    authMethod,
  )

  const isConnectionLimited = isOnTrial || isFreeNewBufferPlan

  const dispatch = useDispatch()
  const { trackCTAClicked } = UpgradePathsTracking(user)

  // We display slightly different copy depending on if the user is a MP customer or
  // if they are New Buffer on the Free Plan
  const heading = isFreeNewBufferPlan
    ? 'Upgrade your Buffer plan'
    : 'Add more channels'
  const planNaming = isFreeNewBufferPlan ? 'current' : 'legacy'
  const extraCopy = isFreeNewBufferPlan
    ? 'To connect more channels, please upgrade your plan.'
    : 'To add more channels, update your plan to unlock our newest features and more channels.'

  const ctaLabel = 'Upgrade Plan'

  // Track when the modal is viewed
  useEffect(() => {
    if (isConnectionLimited) {
      trackUpgradePathViewed({
        payload: {
          upgradePathName: 'channelConnectionsPaywall-upgrade',
          cta: 'channelLimitModal-channelLimitModal-upgradePlan-1',
        },
        user,
      })
    }
  }, [isConnectionLimited, user])

  function closeModal(): void {
    if (onClose) {
      onClose()
    } else {
      dispatch(resetChannelConnectionsState({}))
    }
  }

  function handleOnClick(): void {
    // Track CTA clicked
    if (isConnectionLimited) {
      trackCTAClicked({
        ctaButton: 'upgradePlan',
        ctaView: 'channelLimitModal',
        ctaLocation: 'channelLimitModal',
        upgradePathName: 'channelConnectionsPaywall-upgrade',
      })
    }

    closeModal()
    if (isOneBufferOrg) {
      openModal({
        key: MODALS.planSelector,
        data: {
          cta: 'channelConnections-paywall-upgradePlan-1',
          upgradePathName: 'channelConnectionsPaywall-upgrade',
        },
      })
    } else {
      // Legacy (non-OneBuffer) orgs can no longer self-migrate via the removed
      // Migration Hub; send them to billing settings to manage their plan.
      redirectToUrl(
        getBillingPageUrl('channelConnections-paywall-upgradePlan-1'),
      )
    }
  }

  if (isMPRestrictedService && service) {
    return (
      <PaywallPromptChannels
        service={service as PaywallService}
        authMethod={authMethod}
        handleOnClick={handleOnClick}
        closeModal={closeModal}
      />
    )
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(open: boolean): void => {
        if (!open) {
          closeModal()
        }
      }}
    >
      <Dialog.Content size="medium">
        <Dialog.Header>
          <Dialog.Title>
            {isConnectionLimited
              ? 'No more channel connections available'
              : heading}
          </Dialog.Title>
          <VisuallyHidden>
            <Dialog.Description>
              Manage your channel connections and upgrade options.
            </Dialog.Description>
          </VisuallyHidden>
        </Dialog.Header>
        <Dialog.Body>
          {isConnectionLimited && (
            <Text as="p">
              You have reached the limit on the number of times you can connect
              a channel on{' '}
              {isOnTrial ? 'your free trial' : "Buffer's free plan"}. To add
              more, please consider a paid plan which also unlocks additional
              features.
            </Text>
          )}
          {!isConnectionLimited && (
            <Text as="p">
              You&apos;ve reached the limit on the number of channels you can
              connect based on your {planNaming} plan. {extraCopy}
            </Text>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          <Button
            variant="tertiary"
            onClick={(): void => {
              closeModal()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={(): void => {
              handleOnClick()
            }}
          >
            {ctaLabel}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default PaywallModal
